import { useTranslation } from 'react-i18next'

import styles from './StepVisualScoring.module.scss'

/**
 * Шаг 03 — сигналы графом, а не стеной чипов.
 *
 * Первая версия перечисляла четыре оси оценки, вторая — два десятка чипов по категориям. Обе
 * читались как список: категории были верные, но их было столько, что смысл тонул. Здесь один
 * узел-человек, четыре ветки по видам сигнала и облако безымянных точек за ними. Подписей ровно
 * четыре — по одной на вид, этого хватает, чтобы показать разнообразие, не превращая карточку в
 * перечень.
 *
 * КАТЕГОРИИ ВЗЯТЫ У ПРОДУКТА: это его собственная разметка `app.lead.kinds.*` — «They asked»,
 * «What they can do», «What they are», «What happened». Соцсети движок не читает, у него пять
 * источников и все перечислены в `app.lead.sources.*`, поэтому «social media signals» здесь нет.
 *
 * «Сотни» измерены: 657 уникальных тегов на 929 срабатываний по 821 профилю на проде. Числа на
 * карточке нет намеренно — продукт такого счётчика не рисует, а масштаб несёт облако точек.
 *
 * Подписи веток — настоящие теги из выборки самых частых, КРОМЕ первой. «marketing automation»
 * поставлено по просьбе клиента вместо измеренного «seeking a CTO»: тот читался как наём, а не как
 * нужда в продукте. Такого тега в данных нет — среди 28 уникальных ask-тегов выборки нет ни одного
 * про маркетинг или автоматизацию, там сплошь «seeking clients», «fractional cfo request»,
 * «remote role seeking». Это согласуется с тем, что на Intch просят почти всегда роль, а не
 * покупку. Подпись показывает, что движок УМЕЕТ читать, а не то, что он тут чаще всего находит.
 *
 * Без слова «seeking»: с ним подпись мерила 337 единиц при ширине кадра 320 и уезжала за край
 * (замерено getBBox). Слово и не нужно — его несёт метка «THEY ASKED» прямо над строкой, и три
 * соседние ветки свою категорию тоже не повторяют: «fractional CFO», «decision maker»,
 * «raised seed».
 */
const HUB = { x: 52, y: 90 }

/* Ключи собираются из поля `of`, поэтому проверка i18n их не видит. Здесь они перечислены явно:
   app.lead.kinds.ask, app.lead.kinds.capability, app.lead.kinds.trait, app.lead.kinds.event —
   это собственная разметка продукта, а не наши слова. */
const BRANCHES = [
  { kind: 'ask', of: 'app.lead.kinds.ask', label: 'marketing automation', y: 30 },
  { kind: 'can', of: 'app.lead.kinds.capability', label: 'fractional CFO', y: 70 },
  { kind: 'are', of: 'app.lead.kinds.trait', label: 'decision maker', y: 110 },
  { kind: 'happened', of: 'app.lead.kinds.event', label: 'raised seed', y: 150 },
] as const

/* Облако остальных: те же ветки, но без подписей. Держится ЛЕВЕЕ x=145 — правая половина отдана
   четырём подписям, иначе безымянные точки лезли поверх текста и граф читался как каша.
   Координаты записаны, а не сгенерированы: случайные точки при каждой сборке давали бы другую
   картинку. */
const CLOUD = [
  [96, 14], [124, 30], [88, 42], [132, 62], [100, 74], [140, 86],
  [92, 104], [130, 118], [104, 138], [136, 152], [86, 164], [116, 172],
  [70, 24], [64, 156],
] as const

export const StepVisualScoring = () => {
  const { t } = useTranslation()

  return (
    <div className={styles.root}>
      <svg viewBox="0 0 320 180" aria-hidden focusable="false">
        {/* Ветки без подписей — их больше, чем показано; они уходят под чипы и за край. */}
        <g className={styles.faint}>
          {CLOUD.map(([x, y]) => (
            <line key={`l-${x}-${y}`} x1={HUB.x} y1={HUB.y} x2={x} y2={y} />
          ))}
        </g>
        <g className={styles.dot}>
          {CLOUD.map(([x, y]) => (
            <circle key={`d-${x}-${y}`} cx={x} cy={y} r={2.4} />
          ))}
        </g>

        {/* Четыре названные ветки. */}
        <g className={styles.wire}>
          {BRANCHES.map((branch) => (
            <path
              key={branch.kind}
              d={`M ${HUB.x} ${HUB.y} C ${HUB.x + 46} ${HUB.y} ${146} ${branch.y} ${170} ${branch.y}`}
            />
          ))}
        </g>

        {BRANCHES.map((branch) => (
          <g key={branch.kind} className={styles[branch.kind]}>
            <circle className={styles.node} cx={170} cy={branch.y} r={3.4} />
            {/* Вид сигнала крошечной подписью над значением: без неё четыре цветных узла — просто
                четыре случайных тега, а с ней видно, что движок читает РАЗНОЕ. */}
            <text className={styles.of} x={182} y={branch.y - 5}>
              {t(branch.of)}
            </text>
            <text className={styles.label} x={182} y={branch.y + 8}>
              {branch.label}
            </text>
          </g>
        ))}

        {/* Человек, у которого всё это прочитано. */}
        <circle className={styles.hub} cx={HUB.x} cy={HUB.y} r={17} />
        <g className={styles.glyph} clipPath="url(#scoringHub)">
          <circle cx={HUB.x} cy={HUB.y - 5} r={5.6} />
          <circle cx={HUB.x} cy={HUB.y + 13} r={10.4} />
        </g>
        <defs>
          <clipPath id="scoringHub">
            <circle cx={HUB.x} cy={HUB.y} r={17} />
          </clipPath>
        </defs>
      </svg>
    </div>
  )
}
